import type { ReactNode } from 'react';

import i18next from 'i18next';
import { useEffect, useMemo, useState } from 'react';

import type { UserLanguage } from '~/api/user/types';

import { env } from '~/constants/env';
import { useCraftLocalStorage } from '~/hooks/useCraftLocalStorage';

import { LanguageContext } from './LanguageProvider.context';

type Props = {
  children: ReactNode;
};

export const LanguageProvider = ({ children }: Props) => {
  const [storedLanguage, setStoredLanguage] = useCraftLocalStorage('language');
  const [previewLanguage, setPreviewLanguage] = useState<UserLanguage | null>(null);

  const activeLanguage = previewLanguage ?? storedLanguage ?? 'nl';

  const context = useMemo(
    () => ({
      language: activeLanguage,
      setLanguage: setStoredLanguage,
      setPreviewLanguage,
    }),
    [activeLanguage, setStoredLanguage, setPreviewLanguage],
  );

  useApplyLanguage(activeLanguage);

  return <LanguageContext value={context}>{children}</LanguageContext>;
};

const useApplyLanguage = (language: UserLanguage) => {
  useEffect(() => {
    if (env.VITE_MODE === 'testing') {
      if (i18next.language !== 'cimode') i18next.changeLanguage('cimode');
      return;
    }

    if (language === i18next.language) return;

    i18next.changeLanguage(language);
    document.documentElement.lang = language;
  }, [language]);
};
